/**
 * Utilitário padronizado para download de PDF de contrato.
 * Suporta resposta da API em binário (ArrayBuffer), base64 (string) ou JSON com base64 ({ data: string }).
 */

const PDF_MAGIC = new Uint8Array([0x25, 0x50, 0x44, 0x46]); // %PDF
const _JSON_FIRST_BYTE = 0x7b; // '{'

function isPdfArrayBuffer(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 4) return false;
  const view = new Uint8Array(buffer, 0, 4);
  return view[0] === PDF_MAGIC[0] && view[1] === PDF_MAGIC[1] && view[2] === PDF_MAGIC[2] && view[3] === PDF_MAGIC[3];
}

function base64ToBlob(base64: string): Blob {
  const normalized = String(base64)
    .replace(/^data:application\/pdf;base64,/, '')
    .replace(/[^A-Za-z0-9+/=]/g, '');
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'application/pdf' });
}

/**
 * Converte a resposta da API de download (ArrayBuffer, string base64 ou objeto com data) em Blob PDF.
 */
export function buildPdfBlobFromApiResponse(
  data: ArrayBuffer | Blob | string | { data?: string } | null | undefined
): Blob {
  if (data == null) {
    throw new Error('Resposta de download vazia');
  }
  if (data instanceof Blob) {
    return data;
  }
  if (data instanceof ArrayBuffer) {
    if (isPdfArrayBuffer(data)) {
      return new Blob([data], { type: 'application/pdf' });
    }
    const str = new TextDecoder().decode(data);
    const parsed = JSON.parse(str) as { data?: string };
    const base64 = parsed?.data ?? (typeof parsed === 'string' ? parsed : null);
    if (base64) return base64ToBlob(base64);
    throw new Error('Resposta em JSON sem campo de base64');
  }
  if (typeof data === 'string') {
    return base64ToBlob(data);
  }
  if (typeof data === 'object' && typeof (data as { data?: string }).data === 'string') {
    return base64ToBlob((data as { data: string }).data);
  }
  throw new Error('Formato de resposta de download não suportado');
}

/**
 * Dispara o download do PDF no navegador (cria link, clica, remove e revoga URL).
 */
export function triggerPdfDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 200);
}
